/**
 * Auto-trader counts, mirroring firebase-functions `utils/alerts/autoTradeLimits.ts`.
 * `setAlertAutoTradeEnabled` enforces the same rule; this only drives the UI.
 *
 * - `maxDTUAutoTradeAlertsAllowed` ("Basic" in the UI): DTU standard alerts only
 * - `maxAutoTradeAlertsAllowed`: any alert
 */

export type AutoTradeLimits = { dtu: number; any: number }

// The tradingPlans fields that auto-trade entitlement reads
export type AutoTradeAlert = {
  id?: string
  alertTypes?: string[]
  alertTemplateId?: string
  pattern?: { tradingHouse?: string }
  trading?: { enabled?: boolean }
}

export type AutoTraderPool = { used: number; limit: number }
export type AutoTraderUsage = { dtu: AutoTraderPool; any: AutoTraderPool }

// Anything that isn't a finite positive number counts as 0, as on the server
function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

export function getAutoTradeLimits(alertsProduct?: {
  maxDTUAutoTradeAlertsAllowed?: unknown
  maxAutoTradeAlertsAllowed?: unknown
}): AutoTradeLimits {
  return {
    dtu: toCount(alertsProduct?.maxDTUAutoTradeAlertsAllowed),
    any: toCount(alertsProduct?.maxAutoTradeAlertsAllowed),
  }
}

function isDtuAlert(alert: AutoTradeAlert): boolean {
  return !!alert.alertTemplateId && alert.pattern?.tradingHouse === 'gtf'
}

function isAutoTradeOn(alert: AutoTradeAlert): boolean {
  const types = alert.alertTypes ?? []
  return types.includes('webhook') && !types.includes('exitTradingPlan')
}

// Alerts that use a slot. Per-alert grants (trading.enabled) sit outside the counts, except the
// TPA pointer alert, whose grant the DTU count replaces.
export function countedAlerts<T extends AutoTradeAlert>(
  alerts: T[],
  tpaPointerAlertId?: string
): T[] {
  return alerts.filter(
    (alert) =>
      isAutoTradeOn(alert) && (alert.trading?.enabled !== true || alert.id === tpaPointerAlertId)
  )
}

function tally(counted: AutoTradeAlert[]) {
  const dtuOn = counted.filter(isDtuAlert).length
  return { dtuOn, otherOn: counted.length - dtuOn }
}

// DTU alerts fill Basic slots first and spill into any-alert slots
export function getAutoTraderUsage(
  counted: AutoTradeAlert[],
  limits: AutoTradeLimits
): AutoTraderUsage {
  const { dtuOn, otherOn } = tally(counted)
  const dtuUsed = Math.min(dtuOn, limits.dtu)
  return {
    dtu: { used: dtuUsed, limit: limits.dtu },
    any: { used: otherOn + dtuOn - dtuUsed, limit: limits.any },
  }
}

/** Whether turning auto-trade on for `alert` fits the limits (the server's QUOTA_EXCEEDED check). */
export function fitsAutoTradeLimits(
  alert: AutoTradeAlert,
  alerts: AutoTradeAlert[],
  limits: AutoTradeLimits,
  tpaPointerAlertId?: string
): boolean {
  const alertOn = { ...alert, alertTypes: [...(alert.alertTypes ?? []), 'webhook'] }
  const others = alerts.filter((other) => other.id !== alert.id)
  const counted = countedAlerts([...others, alertOn], tpaPointerAlertId)
  const { otherOn } = tally(counted)
  return otherOn <= limits.any && counted.length <= limits.dtu + limits.any
}

/**
 * False when the user's only slots are Basic and `alert` isn't a DTU standard alert.
 *
 * Answers only whether the slots rule this alert out, not whether the user may auto-trade
 * at all: call it behind the AUTOTRADING feature check. With no counts (`dtu` and `any`
 * both 0) it returns true, which keeps tag-granted auto-trading working on every alert.
 */
export function limitsCoverAlert(alert: AutoTradeAlert, limits: AutoTradeLimits): boolean {
  return limits.any > 0 || limits.dtu === 0 || isDtuAlert(alert)
}
